import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Download, ExternalLink, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { reportsApi } from '../api';
import { centsToDollarString, statusLabel, fmtDate } from '../constants';
import { bpsToPercent } from '../financialConstants';
import { signedMoney } from '../quoteConstants';

const money = (c, fb = '—') => (c == null ? fb : centsToDollarString(c));
const dollars = (c) => (c == null ? '' : (c / 100).toFixed(2));
const pct = (bps) => (bps == null ? '—' : bpsToPercent(bps));
const profitCls = (c) => (c == null ? 'text-gray-500' : c > 0 ? 'text-green-400' : c < 0 ? 'text-red-400' : 'text-gray-200');
const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';

const exportCsv = (filename, headers, rows) => {
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [headers.map((h) => esc(h.label)).join(','), ...rows.map((r) => headers.map((h) => esc(h.get(r))).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'profitability', label: 'Project Profitability' },
  { id: 'estimate-vs-actual', label: 'Estimate vs Actual' },
  { id: 'revenue-changes', label: 'Revenue Changes' },
  { id: 'cost-mix', label: 'Cost Mix' },
  { id: 'responses', label: 'Customer Responses' },
  { id: 'lessons', label: 'Closeout Lessons' },
];

const useReport = (fetcher, params) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    setLoading(true);
    fetcher(params)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load report'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { data, loading };
};

const Spinner = () => <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#D4AF37]" size={26} /></div>;
const Empty = ({ text, testId }) => <Card className="bg-[#111111] border-gray-800 p-10 text-center text-gray-500 text-sm" data-testid={testId}>{text}</Card>;
const StatCard = ({ label, value, sub, accent, testId }) => (
  <Card className="bg-[#111111] border-gray-800 p-4" data-testid={testId}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-lg font-bold mt-1 ${accent || 'text-white'}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-600 mt-0.5">{sub}</div>}
  </Card>
);
const FinBadge = ({ closed }) => (
  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${closed ? 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40' : 'bg-blue-900/30 text-blue-400 border-blue-800'}`}>
    {closed ? 'Final' : 'Projected'}
  </span>
);
const ExportBtn = ({ onClick, testId }) => (
  <Button variant="outline" size="sm" onClick={onClick} className="border-gray-700 text-gray-300 hover:text-[#D4AF37] hover:border-[#D4AF37]" data-testid={testId}>
    <Download size={13} /> Export CSV
  </Button>
);
const SectionNote = ({ children }) => <p className="text-[11px] text-gray-600 mt-2">{children}</p>;

/* ---------- Overview ---------- */
const OverviewReport = () => {
  const { data, loading } = useReport(reportsApi.executiveSummary);
  if (loading) return <Spinner />;
  if (!data) return null;
  const o = data.open, c = data.closed, q = data.quotes, co = data.change_orders;
  return (
    <div className="space-y-6" data-testid="report-overview">
      <div>
        <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-3">Open Work (Projected)</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Active Projects" value={o.active_project_count} testId="ov-active-projects" />
          <StatCard label="Revenue Basis (Budget + COs)" value={money(o.open_revenue_basis_cents)} testId="ov-open-contract" />
          <StatCard label="Total Known Costs" value={money(o.open_total_costs_cents)} testId="ov-open-known-cost" />
          <StatCard label="Potential Gross Profit" value={o.potential_profit_project_count > 0 ? money(o.open_potential_gross_profit_cents) : 'No budgets set'} accent={profitCls(o.open_potential_gross_profit_cents)} sub={`${o.potential_profit_project_count} of ${o.active_project_count} active projects have a customer budget`} testId="ov-projected-gp" />
        </div>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-3">Closed Work (Final)</h4>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard label="Closed Projects" value={c.closed_project_count} testId="ov-closed-count" />
          <StatCard label="Final Contract Value" value={money(c.final_contract_value_cents)} testId="ov-final-contract" />
          <StatCard label="Final Project Cost" value={money(c.final_project_cost_cents)} testId="ov-final-cost" />
          <StatCard label="Final Gross Profit" value={money(c.final_gross_profit_cents)} accent={profitCls(c.final_gross_profit_cents)} testId="ov-final-gp" />
          <StatCard label="Final Gross Margin" value={pct(c.portfolio_final_gross_margin_bps)} sub="Portfolio: total final GP ÷ total final contract" testId="ov-final-margin" />
        </div>
        {c.closed_project_count === 0 && <SectionNote>No financially closed Projects yet.</SectionNote>}
      </div>
      <div>
        <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-3">Customer Response</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Quotes Awaiting" value={q.sent_quote_count} sub={`${q.sent_quote_not_viewed_count} not viewed · ${q.expired_sent_quote_count} expired`} testId="ov-quotes-awaiting" />
          <StatCard label="Change Orders Awaiting" value={co.sent_change_order_count} sub={`${co.sent_change_order_not_viewed_count} not viewed · ${co.expired_sent_change_order_count} expired`} testId="ov-cos-awaiting" />
        </div>
      </div>
      <SectionNote>Projected and Final values are never combined. Open projects use live financial data; closed projects use the immutable Closeout snapshot.</SectionNote>
    </div>
  );
};

/* ---------- Project Profitability ---------- */
const SORTS = [
  { v: 'name', label: 'Project A–Z' },
  { v: 'gp_desc', label: 'Highest Gross Profit' },
  { v: 'gp_asc', label: 'Lowest Gross Profit' },
  { v: 'margin_desc', label: 'Highest Margin' },
  { v: 'margin_asc', label: 'Lowest Margin' },
  { v: 'contract_desc', label: 'Highest Contract Value' },
  { v: 'completed_desc', label: 'Most Recently Completed' },
  { v: 'updated_desc', label: 'Most Recently Updated' },
];
const rowGp = (r) => (r.financial_status === 'financially_closed' ? r.final_gross_profit_cents : r.potential_gross_profit_cents);
const rowMargin = (r) => (r.financial_status === 'financially_closed' ? r.final_gross_margin_bps : r.profit_pct_bps);
const rowContract = (r) => (r.financial_status === 'financially_closed' ? r.final_contract_value_cents : r.current_contract_value_cents);
const rowCost = (r) => (r.financial_status === 'financially_closed' ? r.final_project_cost_cents : r.known_project_cost_cents);

const ProfitabilityReport = () => {
  const [filters, setFilters] = useState({ financial_status: '', search: '', sort: 'name' });
  const { data, loading } = useReport(reportsApi.profitability, { financial_status: filters.financial_status || undefined, search: filters.search || undefined });
  if (loading) return <Spinner />;
  const rows = [...(data?.rows || [])].sort((a, b) => {
    const nn = (v) => (v == null ? -Infinity : v);
    switch (filters.sort) {
      case 'gp_desc': return nn(rowGp(b)) - nn(rowGp(a));
      case 'gp_asc': return nn(rowGp(a) == null ? Infinity : rowGp(a)) - nn(rowGp(b) == null ? Infinity : rowGp(b));
      case 'margin_desc': return nn(rowMargin(b)) - nn(rowMargin(a));
      case 'margin_asc': return nn(rowMargin(a) == null ? Infinity : rowMargin(a)) - nn(rowMargin(b) == null ? Infinity : rowMargin(b));
      case 'contract_desc': return nn(rowContract(b)) - nn(rowContract(a));
      case 'completed_desc': return (b.actual_completion_date || '') > (a.actual_completion_date || '') ? 1 : -1;
      case 'updated_desc': return (b.updated_at || '') > (a.updated_at || '') ? 1 : -1;
      default: return (a.project_name || '').localeCompare(b.project_name || '');
    }
  });
  const doExport = () => exportCsv('project-profitability.csv', [
    { label: 'Project', get: (r) => r.project_name },
    { label: 'Financial Status', get: (r) => (r.financial_status === 'financially_closed' ? 'Final' : 'Open (Projected)') },
    { label: 'Project Status', get: (r) => statusLabel(r.project_status) },
    { label: 'Contract Value ($)', get: (r) => dollars(rowContract(r)) },
    { label: 'Cost ($)', get: (r) => dollars(rowCost(r)) },
    { label: 'Gross Profit ($)', get: (r) => dollars(rowGp(r)) },
    { label: 'Gross Margin (%)', get: (r) => (rowMargin(r) == null ? '' : (rowMargin(r) / 100).toFixed(2)) },
    { label: 'Budget Variance / Health', get: (r) => (r.financial_status === 'financially_closed' ? dollars(r.final_internal_budget_variance_cents) : r.budget_health) },
    { label: 'Completed', get: (r) => r.actual_completion_date || '' },
  ], rows);
  return (
    <div data-testid="report-profitability">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Search projects…" className={`${inputCls} w-full sm:w-52`} data-testid="prof-search" />
        <select value={filters.financial_status} onChange={(e) => setFilters((f) => ({ ...f, financial_status: e.target.value }))} className={selectCls} data-testid="prof-finstatus">
          <option value="">All financial status</option>
          <option value="open">Open</option>
          <option value="financially_closed">Financially Closed</option>
        </select>
        <select value={filters.sort} onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))} className={selectCls} data-testid="prof-sort">
          {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <ExportBtn onClick={doExport} testId="prof-export" />
      </div>
      {rows.length === 0 ? <Empty text="No matching projects." testId="prof-empty" /> : (
        <div className="space-y-2">
          {rows.map((r) => {
            const closed = r.financial_status === 'financially_closed';
            return (
              <Card key={r.project_id} className="bg-[#111111] border-gray-800 p-4" data-testid={`prof-row-${r.project_id}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <div className="md:w-64 min-w-0">
                    <div className="text-sm font-medium text-white truncate flex items-center gap-2">{r.project_name} <FinBadge closed={closed} /></div>
                    <div className="text-xs text-gray-500">{statusLabel(r.project_status)}{closed && r.actual_completion_date ? ` · Completed ${fmtDate(r.actual_completion_date)} · v${r.closeout_version}` : ''}</div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-sm flex-1">
                    <div><span className="text-[10px] text-gray-500 uppercase block">Contract</span><span className="text-gray-200">{money(rowContract(r))}</span></div>
                    <div><span className="text-[10px] text-gray-500 uppercase block">{closed ? 'Final Cost' : 'Known Cost'}</span><span className="text-gray-200">{money(rowCost(r))}</span></div>
                    <div><span className="text-[10px] text-gray-500 uppercase block">{closed ? 'Final GP' : 'Potential GP'}</span><span className={`font-semibold ${profitCls(rowGp(r))}`}>{rowGp(r) != null ? money(rowGp(r)) : 'No budget set'}</span></div>
                    <div><span className="text-[10px] text-gray-500 uppercase block">Margin</span><span className="text-gray-300">{pct(rowMargin(r))}</span></div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <SectionNote>{data?.date_basis}</SectionNote>
    </div>
  );
};

/* ---------- Estimate vs Actual ---------- */
const EstimateVsActualReport = () => {
  const [search, setSearch] = useState('');
  const { data, loading } = useReport(reportsApi.estimateVsActual, { search: search || undefined });
  if (loading) return <Spinner />;
  const rows = data?.rows || [];
  const cov = data?.coverage;
  const doExport = () => exportCsv('estimate-vs-actual.csv', [
    { label: 'Project', get: (r) => r.project_name },
    { label: 'Completed', get: (r) => r.actual_completion_date },
    { label: 'Original Contract ($)', get: (r) => dollars(r.original_contract_value_cents) },
    { label: 'Original Expected Cost ($)', get: (r) => dollars(r.original_internal_cost_budget_cents) },
    { label: 'Original Estimated GP ($)', get: (r) => dollars(r.original_estimated_gross_profit_cents) },
    { label: 'Final Contract ($)', get: (r) => dollars(r.final_contract_value_cents) },
    { label: 'Final Cost ($)', get: (r) => dollars(r.final_project_cost_cents) },
    { label: 'Final GP ($)', get: (r) => dollars(r.final_gross_profit_cents) },
    { label: 'Revenue Change ($)', get: (r) => dollars(r.revenue_change_cents) },
    { label: 'Cost Variance ($)', get: (r) => dollars(r.cost_variance_vs_original_estimate_cents) },
    { label: 'GP Variance ($)', get: (r) => dollars(r.gross_profit_variance_vs_original_estimate_cents) },
  ], rows);
  return (
    <div data-testid="report-eva">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects…" className={`${inputCls} w-full sm:w-52`} data-testid="eva-search" />
        <ExportBtn onClick={doExport} testId="eva-export" />
      </div>
      {cov && (
        <p className="text-xs text-gray-500 mb-4" data-testid="eva-coverage">
          {cov.included_project_count} of {cov.eligible_closed_project_count} closed projects included.
          {cov.excluded_manual_project_count > 0 && ` ${cov.excluded_manual_project_count} manual project${cov.excluded_manual_project_count === 1 ? '' : 's'} excluded (no Original Accepted Quote).`}
          {cov.excluded_missing_original_cost_count > 0 && ` ${cov.excluded_missing_original_cost_count} excluded (original expected cost unknown).`}
        </p>
      )}
      {rows.length === 0 ? <Empty text="No closed quote-converted Projects with complete original expected cost." testId="eva-empty" /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.project_id} className="bg-[#111111] border-gray-800 p-4" data-testid={`eva-row-${r.project_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3">
                <div className="text-sm font-medium text-white">{r.project_name}</div>
                <div className="text-xs text-gray-500">Completed {fmtDate(r.actual_completion_date)}</div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                <div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Original Estimate</div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Contract</span><span className="text-gray-200">{money(r.original_contract_value_cents)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Expected Cost</span><span className="text-gray-200">{money(r.original_internal_cost_budget_cents)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Estimated GP</span><span className="text-gray-200">{money(r.original_estimated_gross_profit_cents)} ({pct(r.original_estimated_gross_margin_bps)})</span></div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Final Actual</div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Contract</span><span className="text-gray-200">{money(r.final_contract_value_cents)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Cost</span><span className="text-gray-200">{money(r.final_project_cost_cents)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Final GP</span><span className={profitCls(r.final_gross_profit_cents)}>{money(r.final_gross_profit_cents)} ({pct(r.final_gross_margin_bps)})</span></div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Variance</div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Revenue Change</span><span className="text-gray-200">{signedMoney(r.revenue_change_cents)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">Cost vs Estimate</span><span className={r.cost_variance_vs_original_estimate_cents > 0 ? 'text-red-400' : 'text-green-400'}>{signedMoney(r.cost_variance_vs_original_estimate_cents)} {r.cost_variance_vs_original_estimate_cents > 0 ? '(over estimate)' : r.cost_variance_vs_original_estimate_cents < 0 ? '(under estimate)' : '(on estimate)'}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-gray-500">GP vs Estimate</span><span className={profitCls(r.gross_profit_variance_vs_original_estimate_cents)}>{signedMoney(r.gross_profit_variance_vs_original_estimate_cents)} {r.gross_profit_variance_vs_original_estimate_cents > 0 ? '(better than estimate)' : r.gross_profit_variance_vs_original_estimate_cents < 0 ? '(worse than estimate)' : '(matched estimate)'}</span></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <SectionNote>{data?.date_basis}</SectionNote>
    </div>
  );
};

/* ---------- Revenue Changes ---------- */
const RevenueChangesReport = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const { data, loading } = useReport(reportsApi.revenueChanges, { search: search || undefined });
  if (loading) return <Spinner />;
  const rows = data?.rows || [];
  const doExport = () => exportCsv('revenue-changes.csv', [
    { label: 'Project', get: (r) => r.project_name },
    { label: 'Financial Status', get: (r) => (r.financial_status === 'financially_closed' ? 'Final' : 'Open') },
    { label: 'Base Contract ($)', get: (r) => dollars(r.base_contract_value_cents) },
    { label: 'Approved COs', get: (r) => r.approved_change_order_count },
    { label: 'Approved Additions ($)', get: (r) => dollars(r.approved_addition_total_cents) },
    { label: 'Approved Deductions ($)', get: (r) => dollars(r.approved_deduction_total_cents) },
    { label: 'Net Approved Change ($)', get: (r) => dollars(r.approved_net_change_cents) },
    { label: 'Current/Final Contract ($)', get: (r) => dollars(r.current_or_final_contract_value_cents) },
  ], rows);
  return (
    <div data-testid="report-revenue-changes">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects…" className={`${inputCls} w-full sm:w-52`} data-testid="rev-search" />
        <ExportBtn onClick={doExport} testId="rev-export" />
      </div>
      {rows.length === 0 ? <Empty text="No matching projects." testId="rev-empty" /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.project_id} className="bg-[#111111] border-gray-800 p-4" data-testid={`rev-row-${r.project_id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 cursor-pointer" onClick={() => setExpanded(expanded === r.project_id ? null : r.project_id)}>
                <div className="md:w-64 min-w-0">
                  <div className="text-sm font-medium text-white truncate flex items-center gap-2">{r.project_name} <FinBadge closed={r.financial_status === 'financially_closed'} /></div>
                  <div className="text-xs text-gray-500">{r.approved_change_order_count} approved change order{r.approved_change_order_count === 1 ? '' : 's'}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-5 gap-y-1 text-sm flex-1">
                  <div><span className="text-[10px] text-gray-500 uppercase block">Base</span><span className="text-gray-200">{money(r.base_contract_value_cents)}</span></div>
                  <div><span className="text-[10px] text-gray-500 uppercase block">Additions</span><span className="text-green-400">{r.approved_addition_total_cents ? `+${money(r.approved_addition_total_cents)}` : '—'}</span></div>
                  <div><span className="text-[10px] text-gray-500 uppercase block">Deductions</span><span className="text-red-400">{r.approved_deduction_total_cents ? `-${money(r.approved_deduction_total_cents)}` : '—'}</span></div>
                  <div><span className="text-[10px] text-gray-500 uppercase block">Net Change</span><span className={profitCls(r.approved_net_change_cents)}>{r.approved_change_order_count ? signedMoney(r.approved_net_change_cents) : '—'}</span></div>
                  <div><span className="text-[10px] text-gray-500 uppercase block">{r.financial_status === 'financially_closed' ? 'Final Contract' : 'Current Contract'}</span><span className="text-[#D4AF37] font-semibold">{money(r.current_or_final_contract_value_cents)}</span></div>
                </div>
              </div>
              {expanded === r.project_id && r.change_orders.length > 0 && (
                <div className="border-t border-gray-800 mt-3 pt-3 space-y-1.5" data-testid={`rev-detail-${r.project_id}`}>
                  {r.change_orders.map((c) => (
                    <div key={c.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-xs bg-[#0D0D0D] rounded px-3 py-2">
                      <span className="text-gray-300">{c.change_order_number} · {c.title} <span className="text-gray-600">approved {c.approved_at ? fmtDate(c.approved_at.slice(0, 10)) : '—'}</span></span>
                      <span className="flex items-center gap-3">
                        <span className="text-green-400">+{money(c.addition_total_cents)}</span>
                        <span className="text-red-400">-{money(c.deduction_total_cents)}</span>
                        <span className={`font-semibold ${profitCls(c.net_change_cents)}`}>{signedMoney(c.net_change_cents)}</span>
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/ops/change-orders/${c.id}`); }} className="text-[#D4AF37] hover:underline" data-testid={`rev-open-co-${c.id}`}><ExternalLink size={12} /></button>
                      </span>
                    </div>
                  ))}
                  <button onClick={() => navigate(`/ops/projects/${r.project_id}?tab=quote-change-orders`)} className="text-xs text-[#D4AF37] hover:underline" data-testid={`rev-open-project-${r.project_id}`}>Open Quote &amp; Change Orders tab →</button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <SectionNote>{data?.date_basis}</SectionNote>
    </div>
  );
};

/* ---------- Cost Mix ---------- */
const MixBar = ({ e, l, s }) => (
  <div className="h-2.5 rounded-full overflow-hidden flex bg-gray-800 w-full">
    {e > 0 && <div className="bg-[#D4AF37]" style={{ width: `${e / 100}%` }} />}
    {l > 0 && <div className="bg-blue-500" style={{ width: `${l / 100}%` }} />}
    {s > 0 && <div className="bg-green-600" style={{ width: `${s / 100}%` }} />}
  </div>
);

const CostMixReport = () => {
  const [filters, setFilters] = useState({ financial_status: '', search: '' });
  const { data, loading } = useReport(reportsApi.costMix, { financial_status: filters.financial_status || undefined, search: filters.search || undefined });
  if (loading) return <Spinner />;
  const rows = (data?.rows || []).filter((r) => r.total_project_cost_cents > 0);
  const zeroCount = (data?.rows || []).length - rows.length;
  const sum = data?.summary;
  const doExport = () => exportCsv('cost-mix.csv', [
    { label: 'Project', get: (r) => r.project_name },
    { label: 'Financial Status', get: (r) => (r.financial_status === 'financially_closed' ? 'Final' : 'Open') },
    { label: 'Expenses ($)', get: (r) => dollars(r.recorded_expenses_cents) },
    { label: 'Employee Labor ($)', get: (r) => dollars(r.recorded_labor_cost_cents) },
    { label: 'Subcontractors ($)', get: (r) => dollars(r.counted_subcontractor_cost_cents) },
    { label: 'Total Direct Cost ($)', get: (r) => dollars(r.total_project_cost_cents) },
    { label: 'Expenses %', get: (r) => (r.expenses_share_bps == null ? '' : (r.expenses_share_bps / 100).toFixed(2)) },
    { label: 'Labor %', get: (r) => (r.labor_share_bps == null ? '' : (r.labor_share_bps / 100).toFixed(2)) },
    { label: 'Subcontractor %', get: (r) => (r.subcontractor_share_bps == null ? '' : (r.subcontractor_share_bps / 100).toFixed(2)) },
  ], rows);
  return (
    <div data-testid="report-cost-mix">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Search projects…" className={`${inputCls} w-full sm:w-52`} data-testid="mix-search" />
        <select value={filters.financial_status} onChange={(e) => setFilters((f) => ({ ...f, financial_status: e.target.value }))} className={selectCls} data-testid="mix-finstatus">
          <option value="">All financial status</option>
          <option value="open">Open</option>
          <option value="financially_closed">Financially Closed</option>
        </select>
        <ExportBtn onClick={doExport} testId="mix-export" />
      </div>
      {sum && sum.total_direct_project_cost_cents > 0 && (
        <Card className="bg-[#111111] border-[#D4AF37]/30 p-4 mb-4" data-testid="mix-summary">
          <div className="text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Portfolio Cost Mix (from totals — not averaged)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-2">
            <div><span className="text-[10px] text-gray-500 uppercase block">Expenses</span><span className="text-gray-200">{money(sum.total_expenses_cents)} ({pct(sum.expenses_share_bps)})</span></div>
            <div><span className="text-[10px] text-gray-500 uppercase block">Employee Labor</span><span className="text-gray-200">{money(sum.total_labor_cost_cents)} ({pct(sum.labor_share_bps)})</span></div>
            <div><span className="text-[10px] text-gray-500 uppercase block">Subcontractors</span><span className="text-gray-200">{money(sum.total_subcontractor_cost_cents)} ({pct(sum.subcontractor_share_bps)})</span></div>
            <div><span className="text-[10px] text-gray-500 uppercase block">Total Direct Cost</span><span className="text-white font-semibold">{money(sum.total_direct_project_cost_cents)}</span></div>
          </div>
          <MixBar e={sum.expenses_share_bps || 0} l={sum.labor_share_bps || 0} s={sum.subcontractor_share_bps || 0} />
          <div className="flex gap-4 text-[10px] text-gray-500 mt-1.5"><span><span className="inline-block w-2 h-2 rounded-full bg-[#D4AF37] mr-1" />Expenses</span><span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1" />Labor</span><span><span className="inline-block w-2 h-2 rounded-full bg-green-600 mr-1" />Subcontractors</span></div>
        </Card>
      )}
      {rows.length === 0 ? <Empty text="No projects with recorded direct costs." testId="mix-empty" /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.project_id} className="bg-[#111111] border-gray-800 p-4" data-testid={`mix-row-${r.project_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                <div className="text-sm font-medium text-white flex items-center gap-2">{r.project_name} <FinBadge closed={r.financial_status === 'financially_closed'} /></div>
                <div className="text-sm text-gray-300">Total {money(r.total_project_cost_cents)}</div>
              </div>
              <MixBar e={r.expenses_share_bps || 0} l={r.labor_share_bps || 0} s={r.subcontractor_share_bps || 0} />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-xs text-gray-400 mt-2">
                <span>Expenses {money(r.recorded_expenses_cents)} ({pct(r.expenses_share_bps)})</span>
                <span>Labor {money(r.recorded_labor_cost_cents)} ({pct(r.labor_share_bps)})</span>
                <span>Subcontractors {money(r.counted_subcontractor_cost_cents)} ({pct(r.subcontractor_share_bps)})</span>
              </div>
            </Card>
          ))}
        </div>
      )}
      {zeroCount > 0 && <SectionNote>{zeroCount} project{zeroCount === 1 ? '' : 's'} with no recorded direct cost hidden (percentages not shown for $0 totals).</SectionNote>}
    </div>
  );
};

/* ---------- Customer Responses ---------- */
const ACTIVITY_LABELS = { not_viewed: 'Not Viewed', viewed_awaiting_response: 'Viewed — Awaiting Response', expired: 'Expired' };
const ACTIVITY_STYLES = { not_viewed: 'bg-gray-800 text-gray-300 border-gray-700', viewed_awaiting_response: 'bg-blue-900/30 text-blue-400 border-blue-800', expired: 'bg-amber-900/30 text-amber-400 border-amber-800' };

const CustomerResponsesReport = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ record_type: '', viewed_status: '', search: '' });
  const { data, loading } = useReport(reportsApi.customerResponses, {
    record_type: filters.record_type || undefined, viewed_status: filters.viewed_status || undefined, search: filters.search || undefined,
  });
  if (loading) return <Spinner />;
  const rows = data?.rows || [];
  const doExport = () => exportCsv('customer-responses.csv', [
    { label: 'Type', get: (r) => (r.record_type === 'quote' ? 'Quote' : 'Change Order') },
    { label: 'Number', get: (r) => r.number },
    { label: 'Title', get: (r) => r.title },
    { label: 'Customer', get: (r) => r.customer_name },
    { label: 'Project', get: (r) => r.project_name || '' },
    { label: 'Sent', get: (r) => (r.sent_at || '').slice(0, 10) },
    { label: 'Valid Until', get: (r) => r.valid_until || '' },
    { label: 'Activity', get: (r) => ACTIVITY_LABELS[r.customer_activity_state] },
    { label: 'Views', get: (r) => r.customer_view_count },
    { label: 'Days Awaiting', get: (r) => r.days_awaiting_response },
  ], rows);
  return (
    <div data-testid="report-responses">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Search…" className={`${inputCls} w-full sm:w-52`} data-testid="resp-search" />
        <select value={filters.record_type} onChange={(e) => setFilters((f) => ({ ...f, record_type: e.target.value }))} className={selectCls} data-testid="resp-type">
          <option value="">All types</option>
          <option value="quote">Quotes</option>
          <option value="change_order">Change Orders</option>
        </select>
        <select value={filters.viewed_status} onChange={(e) => setFilters((f) => ({ ...f, viewed_status: e.target.value }))} className={selectCls} data-testid="resp-viewed">
          <option value="">All activity</option>
          <option value="not_viewed">Not Viewed</option>
          <option value="viewed">Viewed</option>
        </select>
        <ExportBtn onClick={doExport} testId="resp-export" />
      </div>
      {rows.length === 0 ? <Empty text="No Quotes or Change Orders are currently awaiting customer response." testId="resp-empty" /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={`${r.record_type}-${r.id}`} className="bg-[#111111] border-gray-800 p-4" data-testid={`resp-row-${r.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <div className="md:w-72 min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    <span className={`mr-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.record_type === 'quote' ? 'bg-purple-900/40 text-purple-300' : 'bg-teal-900/40 text-teal-300'}`}>{r.record_type === 'quote' ? 'Quote' : 'Change Order'}</span>
                    {r.title}
                  </div>
                  <div className="text-xs text-gray-500">{r.number} · {r.customer_name || 'No customer'}{r.project_name ? ` · ${r.project_name}` : ''}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-1 text-sm flex-1">
                  <div><span className="text-[10px] text-gray-500 uppercase block">Sent</span><span className="text-gray-300">{r.sent_at ? fmtDate(r.sent_at.slice(0, 10)) : '—'}</span></div>
                  <div><span className="text-[10px] text-gray-500 uppercase block">Days Awaiting</span><span className="text-white font-semibold">{r.days_awaiting_response ?? '—'}</span></div>
                  <div><span className="text-[10px] text-gray-500 uppercase block">Activity</span><span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${ACTIVITY_STYLES[r.customer_activity_state]}`}>{ACTIVITY_LABELS[r.customer_activity_state]}{r.customer_view_count > 0 ? ` (${r.customer_view_count})` : ''}</span></div>
                  <div><span className="text-[10px] text-gray-500 uppercase block">Valid Until</span><span className="text-gray-300">{r.valid_until ? fmtDate(r.valid_until) : '—'}</span></div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(r.record_type === 'quote' ? `/ops/quotes/${r.id}` : `/ops/change-orders/${r.id}`)} className="border-gray-700 text-gray-300 hover:text-[#D4AF37] hover:border-[#D4AF37] shrink-0" data-testid={`resp-open-${r.id}`}>
                  <ExternalLink size={13} /> Open
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      <SectionNote>{data?.date_basis} Sorted longest awaiting first.</SectionNote>
    </div>
  );
};

/* ---------- Closeout Lessons ---------- */
const CloseoutLessonsReport = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const { data, loading } = useReport(reportsApi.closeoutLessons, { search: search || undefined, show_all: showAll ? 'true' : undefined });
  if (loading) return <Spinner />;
  const rows = data?.rows || [];
  const doExport = () => exportCsv('closeout-lessons.csv', [
    { label: 'Project', get: (r) => r.project_name },
    { label: 'Type', get: (r) => r.project_type },
    { label: 'Completed', get: (r) => r.actual_completion_date },
    { label: 'Final Contract ($)', get: (r) => dollars(r.final_contract_value_cents) },
    { label: 'Final GP ($)', get: (r) => dollars(r.final_gross_profit_cents) },
    { label: 'Final Margin (%)', get: (r) => (r.final_gross_margin_bps == null ? '' : (r.final_gross_margin_bps / 100).toFixed(2)) },
    { label: 'Pricing Lessons', get: (r) => r.pricing_lessons || '' },
    { label: 'Operational Lessons', get: (r) => r.operational_lessons || '' },
    { label: 'Closeout Notes', get: (r) => r.closeout_notes || '' },
  ], rows);
  return (
    <div data-testid="report-lessons">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search lessons, notes, projects…" className={`${inputCls} w-full sm:w-64`} data-testid="lessons-search" />
        <label className="flex items-center gap-2 text-sm text-gray-400">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="lessons-show-all" /> Show all closed projects
        </label>
        <Button variant="outline" size="sm" onClick={() => navigate('/ops/price-book')} className="border-gray-700 text-gray-300 hover:text-[#D4AF37] hover:border-[#D4AF37]" data-testid="lessons-open-pricebook">Open Price Book</Button>
        <ExportBtn onClick={doExport} testId="lessons-export" />
      </div>
      <p className="text-xs text-gray-500 mb-4">Pricing lessons are for intentional review — the Price Book is never changed automatically. Line-item actual profitability is not tracked, so lessons describe Project-level outcomes only.</p>
      {rows.length === 0 ? <Empty text={showAll ? 'No financially closed Projects yet.' : 'No Pricing Lessons have been recorded yet.'} testId="lessons-empty" /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.project_id} className="bg-[#111111] border-gray-800 p-4" data-testid={`lessons-row-${r.project_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                <div className="text-sm font-medium text-white">{r.project_name}</div>
                <div className="text-xs text-gray-500">Completed {fmtDate(r.actual_completion_date)} · Final GP <span className={profitCls(r.final_gross_profit_cents)}>{money(r.final_gross_profit_cents)}</span> ({pct(r.final_gross_margin_bps)})</div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
                {r.pricing_lessons && <div><div className="text-[10px] text-[#D4AF37] uppercase tracking-wider mb-0.5">Pricing Lessons</div><p className="text-gray-300 whitespace-pre-wrap">{r.pricing_lessons}</p></div>}
                {r.operational_lessons && <div><div className="text-[10px] text-[#D4AF37] uppercase tracking-wider mb-0.5">Operational Lessons</div><p className="text-gray-300 whitespace-pre-wrap">{r.operational_lessons}</p></div>}
                {r.closeout_notes && <div><div className="text-[10px] text-[#D4AF37] uppercase tracking-wider mb-0.5">Closeout Notes</div><p className="text-gray-300 whitespace-pre-wrap">{r.closeout_notes}</p></div>}
                {!r.has_lessons && <p className="text-xs text-gray-600">No lessons recorded for this project.</p>}
              </div>
              <button onClick={() => navigate(`/ops/projects/${r.project_id}?tab=financials`)} className="text-xs text-[#D4AF37] hover:underline mt-2" data-testid={`lessons-open-project-${r.project_id}`}>Open Project →</button>
            </Card>
          ))}
        </div>
      )}
      <SectionNote>{data?.date_basis}</SectionNote>
    </div>
  );
};

/* ---------- Page ---------- */
export default function OpsReports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  return (
    <div data-testid="ops-reports">
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2"><BarChart3 className="text-[#D4AF37]" size={26} /> Reports</h2>
        <p className="text-gray-400 mt-1">Understand Project profitability, estimate accuracy, cost mix, and customer response activity.</p>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-5 -mx-1 px-1">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setSearchParams({ tab: t.id })}
            className={`whitespace-nowrap text-sm px-3.5 py-1.5 rounded-full border transition-colors ${tab === t.id ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-semibold' : 'bg-transparent text-gray-400 border-gray-700 hover:text-white hover:border-gray-500'}`}
            data-testid={`reports-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'overview' && <OverviewReport />}
      {tab === 'profitability' && <ProfitabilityReport />}
      {tab === 'estimate-vs-actual' && <EstimateVsActualReport />}
      {tab === 'revenue-changes' && <RevenueChangesReport />}
      {tab === 'cost-mix' && <CostMixReport />}
      {tab === 'responses' && <CustomerResponsesReport />}
      {tab === 'lessons' && <CloseoutLessonsReport />}
    </div>
  );
}
